import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import ParseContent from 'components/shared/ParseContent'
import Image from 'components/shared/ImageGatsby'
import ButtonDefault from 'components/elements/ButtonDefault'
import { ScrollRevealItem } from 'components/shared/ScrollReveal'

// Third Party
import styled from 'styled-components'

const StyledBannerDefault = styled.section`
  height: calc(100vh - 150px);

  @media (max-width: 991px) {
    height: calc(100vh - 250px);
  }
`

const StyledImage = styled(Image)`
  &.custom {
    @media (min-width: 992px) {
      & img {
        object-position: 50% 90%;
      }
    }
  }

  & img {
    padding-left: 24px !important;
    padding-right: 24px !important;

    @media (max-width: 991px) {
      padding-left: 16px !important;
      padding-right: 16px !important;
    }
  }
`

const FallbackBannerImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;

  &.custom {
    @media (min-width: 992px) {
      object-position: 50% 90%;
    }
  }

  padding-left: 24px !important;
  padding-right: 24px !important;

  @media (max-width: 991px) {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
`

const Content = styled(ParseContent)`
  & h1 {
    font-family: ${(props) => props.theme.font.family.secondary};
    font-size: ${(props) => props.theme.font.size['60']};
    line-height: 70px;
    color: #fff;
    font-weight: 700;
    text-transform: uppercase;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);
    margin-bottom: 23px;

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['32']};
      line-height: 40px;
    }
  }

  & h2 {
    font-family: ${(props) => props.theme.font.family.secondary};
    font-size: ${(props) => props.theme.font.size['25']};
    line-height: 30px;
    color: #fff;
    font-weight: 600;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.25);

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['20']};
      line-height: 25px;
    }
  }

  & p {
    font-family: ${(props) => props.theme.font.family.secondary};
    font-size: ${(props) => props.theme.font.size['25']};
    color: #fff;
    font-weight: 600;
    filter: drop-shadow(0px 1px 4px rgba(0, 0, 0, 0.25));
    text-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);

    @media (max-width: 991px) {
      font-size: ${(props) => props.theme.font.size['16']};
      line-height: 20px;
    }
  }
`

const StyledButtonDefault = styled(ButtonDefault)`
  & > a:hover,
  & > button:hover {
    background-color: #fff;
    border-color: #fff;
    color: #371216;
  }
`

interface BannerBigProps {
  // eslint-disable-next-line
  fields: GatsbyTypes.WpPost_Flexcontent_Flex_Banner
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

function hasRenderableBannerImage(img: any): boolean {
  if (!img?.localFile) return false
  if (img.localFile.childImageSharp?.gatsbyImageData) return true
  if (img.localFile.publicURL) return true
  return false
}

function buildBannerCandidates(fields: BannerBigProps['fields']) {
  const fromGallery = (fields.images ?? []).filter(hasRenderableBannerImage)
  if (fromGallery.length > 0) return fromGallery
  if (fields.image && hasRenderableBannerImage(fields.image)) {
    return [fields.image]
  }
  return []
}

function bannerImageKey(img: any) {
  const g =
    img?.localFile?.childImageSharp?.gatsbyImageData?.images?.fallback?.src
  const u = img?.localFile?.publicURL
  return g ?? u ?? ''
}

function BannerBigImage({
  image,
  positionImage,
  className,
  loading,
}: {
  image: any
  positionImage: boolean
  className: string
  loading: 'lazy' | 'eager'
}) {
  if (image?.localFile?.childImageSharp?.gatsbyImageData) {
    return (
      <StyledImage
        alt="Lofthus Banner"
        image={image}
        className={`${className} ${positionImage ? 'custom' : ''}`}
        loading={loading}
      />
    )
  }
  const url = image?.localFile?.publicURL
  if (url) {
    return (
      <FallbackBannerImage
        alt="Lofthus Banner"
        src={url}
        className={`${className} ${positionImage ? 'custom' : ''}`}
        loading={loading}
      />
    )
  }
  return null
}

function BannerBig({ fields }: BannerBigProps) {
  const imageCandidates = useMemo(
    () => buildBannerCandidates(fields),
    [fields.images, fields.image]
  )

  const candidatesSignature = useMemo(() => {
    const parts = (fields.images ?? []).map((img) => {
      const g =
        img?.localFile?.childImageSharp?.gatsbyImageData?.images?.fallback
          ?.src ?? ''
      const u = img?.localFile?.publicURL ?? ''
      return `${g}|${u}`
    })
    const single =
      fields.image?.localFile?.childImageSharp?.gatsbyImageData?.images
        ?.fallback?.src ??
      fields.image?.localFile?.publicURL ??
      ''
    return `${parts.join('~')}::${single}`
  }, [fields.images, fields.image])

  const [imageIndex, setImageIndex] = useState(0)

  useIsomorphicLayoutEffect(() => {
    const list = buildBannerCandidates(fields)
    if (list.length <= 1) {
      setImageIndex(0)
      return
    }
    setImageIndex(Math.floor(Math.random() * list.length))
  }, [candidatesSignature])

  const bannerImage = imageCandidates[imageIndex] ?? null

  return (
    <StyledBannerDefault
      className={`${
        fields?.hideOnMobile === true ? 'd-none d-lg-flex' : 'd-flex'
      } position-relative align-items-center`}
    >
      {bannerImage ? (
        <BannerBigImage
          key={bannerImageKey(bannerImage)}
          image={bannerImage}
          positionImage={Boolean(fields.positionImage)}
          className="position-absolute h-100 w-100"
          loading="lazy"
        />
      ) : null}

      <div className="container h-100 d-flex flex-column" style={{ zIndex: 2 }}>
        <div
          className="flex-grow-1 d-flex flex-column justify-content-center align-items-center"
          style={{ minHeight: 0 }}
        >
          {fields.description && (
            <Content content={fields.description} className="mb-0" />
          )}
        </div>
        {fields.links && (
          <div
            className="d-flex flex-column flex-lg-row justify-content-center flex-shrink-0 px-5 px-lg-0 pb-4 pb-lg-5 position-relative"
            style={{ gap: '24px', zIndex: 2 }}
          >
            {fields.links?.map((link, linkIndex: number) => {
              const item = link?.link
              if (!item?.title) return null
              return (
                <ScrollRevealItem
                  key={item.title}
                  sequenceIndex={linkIndex}
                  className="d-inline-flex w-auto"
                >
                  <StyledButtonDefault to={item.url || '/'}>
                    {item.title}
                  </StyledButtonDefault>
                </ScrollRevealItem>
              )
            })}
          </div>
        )}
      </div>
    </StyledBannerDefault>
  )
}

export default BannerBig
